<script lang="ts" setup>
import type { Ref } from 'vue';
import type { PinchOrigin } from '#layers/assets/composables/use-pinch';
import type { MonospaceFile } from '#layers/data-model/public';
import type { ButtonProps } from '#layers/ui/public';
import { createReusableTemplate } from '@vueuse/core';
import { FocusScope, useBodyScrollLock } from 'reka-ui';
import { computed, nextTick, onBeforeUnmount, ref, useTemplateRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAppConfig } from '#app/config';
import { useWorkspaceAssetUrl } from '#layers/assets/composables/use-asset-url';
import { useFileInfo } from '#layers/assets/composables/use-file-info';
import { usePinch } from '#layers/assets/composables/use-pinch';

interface ZoomToolbarButtonProps extends Partial<ButtonProps> {
	buttonAriaLabel: string;
	buttonClass: string;
	buttonText?: string;
	tooltipText: string;
}

const {
	restoreFocusOnClose = true,
	file,
} = defineProps<{
	file: MonospaceFile;
	restoreFocusOnClose?: boolean;
}>();

const isOpen = defineModel<boolean>('open', { default: false });

const { t } = useI18n();
const appConfig = useAppConfig();
const assetURL = useWorkspaceAssetUrl(() => file);
const quickLookDialog = useTemplateRef<HTMLElement>('quickLookDialog');

const { metadataLine: fileInfoLine } = useFileInfo(() => file);
const imageAlt = computed(() => file.title || file.fileName || t('common.preview'));
const quickLookTooltipContent = { side: 'bottom' } as const;
const quickLookBottomTooltipContent = { side: 'top' } as const;
const quickLookTooltipUi = { content: 'z-1001' };

const [DefineZoomToolbarButton, ReuseZoomToolbarButton] = createReusableTemplate<ZoomToolbarButtonProps>({ inheritAttrs: false });

const {
	isBackgroundInverted,
	quickLookBackgroundClass,
	backgroundToggleLabel,
	toggleBackground,
} = useQuickLookBackground();

const {
	zoomLevel,
	zoomPercent,
	scrollContentPercent,
	imageFramePercent,
	resetZoomLabel,
	resetPinch,
	zoomIn,
	zoomOut,
	resetZoom,
	cancelPan,
	handlePanStart,
	handleStageClick,
	stageInteractionClass,
} = useQuickLookZoom(isOpen);

const {
	closeQuickLook,
} = useQuickLookDialog(isOpen, { cancelPan, resetPinch, resetZoom });

async function handleQuickLookAutoFocus(event: Event) {
	event.preventDefault();

	await nextTick();

	quickLookDialog.value?.focus({ preventScroll: true });
}

function handleQuickLookRestoreFocus(event: Event) {
	if (restoreFocusOnClose) return;

	event.preventDefault();
}

function useQuickLookBackground() {
	const { t } = useI18n();
	const isBackgroundInverted = ref(false);
	const quickLookBackgroundClass = computed(() => isBackgroundInverted.value ? 'bg-inverted' : 'bg-default');
	const backgroundToggleLabel = computed(() => isBackgroundInverted.value ? t('file_preview.image.use_default_background') : t('file_preview.image.use_inverted_background'));

	function toggleBackground() {
		isBackgroundInverted.value = !isBackgroundInverted.value;
	}

	return {
		isBackgroundInverted,
		quickLookBackgroundClass,
		backgroundToggleLabel,
		toggleBackground,
	};
}

function useQuickLookZoom(isOpen: Ref<boolean>) {
	const { t } = useI18n();
	const zoomLevel = ref(1);
	const pinchStartZoom = ref(1);
	let zoomOperationId = 0;
	const quickLookStageRef = useTemplateRef<HTMLElement>('quickLookStage');
	const quickLookImageFrameRef = useTemplateRef<HTMLElement>('quickLookImageFrame');
	const pinchTarget = computed(() => {
		if (!isOpen.value || typeof window === 'undefined') return null;

		return window;
	});

	const minZoomLevel = 0.25;
	const maxZoomLevel = 4;
	const zoomStep = 0.25;

	const zoomPercent = computed(() => Math.round(zoomLevel.value * 100));
	const scrollContentPercent = computed(() => Math.max(zoomPercent.value, 100));
	const imageFramePercent = computed(() => zoomLevel.value > 1 ? 100 : zoomPercent.value);
	const resetZoomLabel = computed(() => t('file_preview.image.reset_zoom_with_current', { zoom: zoomPercent.value }));
	const {
		cancelPan,
		handlePanStart,
		handleStageClick,
		stageInteractionClass,
	} = useQuickLookPan(quickLookStageRef, zoomLevel);

	const {
		resetPinch,
	} = usePinch(pinchTarget, {
		onPinchStart: () => {
			cancelPan();
			pinchStartZoom.value = zoomLevel.value;
		},
		onPinch: ({ origin, scale }) => {
			const previousZoom = zoomLevel.value;
			const nextZoom = clampZoom(pinchStartZoom.value * scale);

			zoomLevel.value = nextZoom;
			preserveZoomOrigin(origin, previousZoom, nextZoom);
		},
	});

	watch(isOpen, (open) => {
		if (!open) {
			zoomOperationId++;
			cancelPan();
		}
	});

	function zoomIn() {
		zoomAroundViewportCenter(clampZoom(zoomLevel.value + zoomStep));
	}

	function zoomOut() {
		zoomAroundViewportCenter(clampZoom(zoomLevel.value - zoomStep));
	}

	function resetZoom() {
		zoomOperationId++;
		zoomLevel.value = 1;
	}

	function clampZoom(value: number) {
		return Math.min(Math.max(value, minZoomLevel), maxZoomLevel);
	}

	function zoomAroundViewportCenter(nextZoom: number) {
		const stage = quickLookStageRef.value;
		const previousZoom = zoomLevel.value;

		zoomLevel.value = nextZoom;

		if (!stage) return;

		const rect = stage.getBoundingClientRect();

		preserveZoomOrigin({
			clientX: rect.left + rect.width / 2,
			clientY: rect.top + rect.height / 2,
		}, previousZoom, nextZoom);
	}

	async function preserveZoomOrigin(origin: PinchOrigin, previousZoom: number, nextZoom: number) {
		const stage = quickLookStageRef.value;
		const imageFrame = quickLookImageFrameRef.value;
		if (!stage || previousZoom === nextZoom || previousZoom === 0) return;
		const operationId = ++zoomOperationId;

		// Capture the origin's position within the rendered image before Vue
		// applies the new frame size, then restore that same image point under
		// the pointer/viewport center after the DOM has updated.
		const imageFrameRect = imageFrame?.getBoundingClientRect();
		const relativeX = imageFrameRect?.width ? (origin.clientX - imageFrameRect.left) / imageFrameRect.width : null;
		const relativeY = imageFrameRect?.height ? (origin.clientY - imageFrameRect.top) / imageFrameRect.height : null;
		const stageRect = stage.getBoundingClientRect();
		const viewportX = origin.clientX - stageRect.left;
		const viewportY = origin.clientY - stageRect.top;
		const zoomRatio = nextZoom / previousZoom;
		// If the image frame is not measurable yet, fall back to scroll-space
		// scaling so zoom buttons still preserve the viewport center reasonably.
		const fallbackScrollLeft = (stage.scrollLeft + viewportX) * zoomRatio - viewportX;
		const fallbackScrollTop = (stage.scrollTop + viewportY) * zoomRatio - viewportY;

		await nextTick();

		if (!isOpen.value || operationId !== zoomOperationId) return;

		const currentStage = quickLookStageRef.value;
		const nextImageFrameRect = quickLookImageFrameRef.value?.getBoundingClientRect();
		if (!currentStage) return;

		const nextScrollLeft = relativeX !== null && nextImageFrameRect?.width
			? currentStage.scrollLeft + nextImageFrameRect.left + relativeX * nextImageFrameRect.width - origin.clientX
			: fallbackScrollLeft;
		const nextScrollTop = relativeY !== null && nextImageFrameRect?.height
			? currentStage.scrollTop + nextImageFrameRect.top + relativeY * nextImageFrameRect.height - origin.clientY
			: fallbackScrollTop;

		currentStage.scrollLeft = Math.max(0, nextScrollLeft);
		currentStage.scrollTop = Math.max(0, nextScrollTop);
	}

	return {
		zoomLevel,
		zoomPercent,
		scrollContentPercent,
		imageFramePercent,
		resetZoomLabel,
		resetPinch,
		zoomIn,
		zoomOut,
		resetZoom,
		cancelPan,
		handlePanStart,
		handleStageClick,
		stageInteractionClass,
	};
}

function useQuickLookPan(stageRef: Ref<HTMLElement | null>, zoomLevel: Ref<number>) {
	const isPanning = ref(false);
	const suppressNextStageClick = ref(false);
	let activePan: {
		hasMoved: boolean;
		pointerId: number;
		scrollLeft: number;
		scrollTop: number;
		x: number;
		y: number;
	} | null = null;
	let suppressStageClickTimer: number | null = null;

	const stageInteractionClass = computed(() => {
		if (zoomLevel.value <= 1) return '';

		return isPanning.value ? 'cursor-grabbing select-none' : 'cursor-grab';
	});

	function handlePanStart(event: PointerEvent) {
		const stage = stageRef.value;
		if (!stage || zoomLevel.value <= 1) return;
		if (event.pointerType === 'touch') return;
		if (event.pointerType === 'mouse' && event.button !== 0) return;
		if (event.isPrimary === false) return;

		activePan = {
			hasMoved: false,
			pointerId: event.pointerId,
			scrollLeft: stage.scrollLeft,
			scrollTop: stage.scrollTop,
			x: event.clientX,
			y: event.clientY,
		};

		isPanning.value = true;
		stage.setPointerCapture?.(event.pointerId);
		event.preventDefault();

		window.addEventListener('pointermove', handlePanMove, { passive: false });
		window.addEventListener('pointerup', handlePanEnd, { passive: false });
		window.addEventListener('pointercancel', handlePanEnd, { passive: false });
	}

	function handlePanMove(event: PointerEvent) {
		const stage = stageRef.value;
		if (!stage || !activePan || event.pointerId !== activePan.pointerId) return;

		const deltaX = event.clientX - activePan.x;
		const deltaY = event.clientY - activePan.y;

		if (Math.abs(deltaX) > 3 || Math.abs(deltaY) > 3) {
			activePan.hasMoved = true;
		}

		stage.scrollLeft = activePan.scrollLeft - deltaX;
		stage.scrollTop = activePan.scrollTop - deltaY;
		event.preventDefault();
	}

	function handlePanEnd(event: PointerEvent) {
		if (!activePan || event.pointerId !== activePan.pointerId) return;

		if (activePan.hasMoved) {
			suppressNextStageClick.value = true;

			if (suppressStageClickTimer !== null) {
				window.clearTimeout(suppressStageClickTimer);
			}

			suppressStageClickTimer = window.setTimeout(() => {
				suppressNextStageClick.value = false;
				suppressStageClickTimer = null;
			});
		}

		activePan = null;
		isPanning.value = false;
		removePanListeners();
	}

	function handleStageClick(close: () => void) {
		if (suppressNextStageClick.value) return;

		close();
	}

	function removePanListeners() {
		window.removeEventListener('pointermove', handlePanMove);
		window.removeEventListener('pointerup', handlePanEnd);
		window.removeEventListener('pointercancel', handlePanEnd);
	}

	function cancelPan() {
		activePan = null;
		isPanning.value = false;
		removePanListeners();

		if (suppressStageClickTimer !== null) {
			window.clearTimeout(suppressStageClickTimer);
			suppressStageClickTimer = null;
			suppressNextStageClick.value = false;
		}
	}

	onBeforeUnmount(cancelPan);

	return {
		cancelPan,
		handlePanStart,
		handleStageClick,
		stageInteractionClass,
	};
}

function useQuickLookDialog(
	isOpen: Ref<boolean>,
	controls: {
		cancelPan: () => void;
		resetPinch: () => void;
		resetZoom: () => void;
	},
) {
	const isBodyLocked = useBodyScrollLock(isOpen.value);

	watch(isOpen, (open) => {
		isBodyLocked.value = open;

		if (open) {
			controls.resetZoom();
		}
		else {
			controls.cancelPan();
		}
	}, { immediate: true });

	onBeforeUnmount(() => {
		isBodyLocked.value = false;
	});

	function closeQuickLook() {
		isOpen.value = false;
		controls.cancelPan();
		controls.resetPinch();
	}

	return {
		closeQuickLook,
	};
}
</script>

<template>
	<Teleport to="body">
		<DefineZoomToolbarButton v-slot="{ buttonAriaLabel, buttonClass, buttonText, tooltipText, ...buttonProps }">
			<DTooltip :content="quickLookBottomTooltipContent" :text="tooltipText" :ui="quickLookTooltipUi">
				<DButton
					color="neutral"
					size="xs"
					variant="ghost"
					:class="buttonClass"
					:aria-label="buttonAriaLabel"
					v-bind="buttonProps"
				>
					{{ buttonText }}
				</DButton>
			</DTooltip>
		</DefineZoomToolbarButton>

		<Transition
			enter-active-class="transition-opacity duration-150 ease-out"
			enter-from-class="opacity-0"
			enter-to-class="opacity-100"
			leave-active-class="transition-opacity duration-150 ease-in"
			leave-from-class="opacity-100"
			leave-to-class="opacity-0"
		>
			<FocusScope
				v-if="isOpen"
				as-child
				loop
				trapped
				@mount-auto-focus="handleQuickLookAutoFocus"
				@unmount-auto-focus="handleQuickLookRestoreFocus"
			>
				<div
					ref="quickLookDialog"
					class="pointer-events-auto fixed inset-0 z-1000 flex flex-col outline-none"
					:class="quickLookBackgroundClass"
					role="dialog"
					aria-modal="true"
					:aria-label="$t('file_preview.image.quick_look')"
					tabindex="-1"
					@keydown.esc.stop="closeQuickLook"
					@pointerdown.stop
				>
					<header class="flex h-14 shrink-0 items-center justify-between gap-3 border-b bg-default px-4 text-sm">
						<div class="min-w-0">
							<p v-if="file.fileName" class="truncate font-medium text-default">
								{{ file.fileName }}
							</p>
							<p v-if="fileInfoLine" class="truncate text-xs text-muted">
								{{ fileInfoLine }}
							</p>
						</div>
						<div class="flex shrink-0 items-center gap-1">
							<DTooltip :content="quickLookTooltipContent" :text="backgroundToggleLabel" :ui="quickLookTooltipUi">
								<DButton
									color="neutral"
									size="sm"
									variant="ghost"
									icon="lucide:contrast"
									:pressed="isBackgroundInverted"
									:aria-pressed="isBackgroundInverted"
									:aria-label="backgroundToggleLabel"
									@click="toggleBackground"
								/>
							</DTooltip>
							<DTooltip :content="quickLookTooltipContent" :text="$t('common.download')" :ui="quickLookTooltipUi">
								<DButton
									color="neutral"
									size="sm"
									variant="ghost"
									target="_blank"
									rel="noopener noreferrer"
									download
									:aria-label="$t('common.download')"
									:icon="appConfig.ui.icons.download"
									:href="assetURL"
								/>
							</DTooltip>
							<DTooltip :content="quickLookTooltipContent" :text="$t('common.close')" :ui="quickLookTooltipUi">
								<DButton
									color="neutral"
									size="sm"
									variant="ghost"
									:aria-label="$t('common.close')"
									:icon="appConfig.ui.icons.remove"
									@click="closeQuickLook"
								/>
							</DTooltip>
						</div>
					</header>
					<div
						ref="quickLookStage"
						data-testid="quick-look-stage"
						class="min-h-0 flex-1 overflow-auto p-4 pb-24 sm:p-6 sm:pb-24"
						:class="[quickLookBackgroundClass, stageInteractionClass]"
						@click.self="handleStageClick(closeQuickLook)"
					>
						<div
							data-testid="quick-look-scroll-content"
							class="flex shrink-0"
							:class="zoomLevel > 1 ? 'items-start justify-start' : 'items-center justify-center'"
							:style="{ width: `${scrollContentPercent}%`, height: `${scrollContentPercent}%` }"
							@click.self="handleStageClick(closeQuickLook)"
							@pointerdown="handlePanStart"
						>
							<div
								ref="quickLookImageFrame"
								data-testid="quick-look-image-frame"
								class="flex shrink-0 items-center justify-center"
								:style="{ width: `${imageFramePercent}%`, height: `${imageFramePercent}%` }"
							>
								<img
									:src="assetURL"
									:alt="imageAlt"
									class="size-full rounded-sm object-contain"
								>
							</div>
						</div>
					</div>
					<div
						class="
							pointer-events-none fixed inset-x-0 bottom-[calc(2rem+env(safe-area-inset-bottom))] z-10 flex justify-center px-3
						"
					>
						<div
							class="
								pointer-events-auto flex items-center gap-0.5 rounded-md border bg-default/95 p-0.5 shadow-md backdrop-blur-sm
							"
							role="toolbar"
							:aria-label="$t('file_preview.image.zoom_controls')"
						>
							<ReuseZoomToolbarButton
								:button-aria-label="$t('common.zoom_out')"
								button-class="flex size-9 items-center justify-center sm:size-8"
								:disabled="zoomLevel <= 0.25"
								:icon="appConfig.ui.icons.minus"
								:tooltip-text="$t('common.zoom_out')"
								@click="zoomOut"
							/>
							<ReuseZoomToolbarButton
								:button-aria-label="resetZoomLabel"
								button-class="flex h-9 min-w-16 items-center justify-center px-2 text-center text-xs tabular-nums sm:h-8 sm:min-w-14"
								:button-text="`${zoomPercent}%`"
								:tooltip-text="$t('file_preview.image.reset_zoom')"
								@click="resetZoom"
							/>
							<ReuseZoomToolbarButton
								:button-aria-label="$t('common.zoom_in')"
								button-class="flex size-9 items-center justify-center sm:size-8"
								:disabled="zoomLevel >= 4"
								:icon="appConfig.ui.icons.plus"
								:tooltip-text="$t('common.zoom_in')"
								@click="zoomIn"
							/>
						</div>
					</div>
				</div>
			</FocusScope>
		</Transition>
	</Teleport>
</template>
